// stores/artifact-store.ts
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { ArtifactItem, ArtifactLoadingPlaceholder, ArtifactType } from "@/types/Artifact";

interface ArtifactState {
    // UI State
    isOpen: boolean;
    activeArtifactId: string | null;
    panelWidth: number;

    // Data State
    artifacts: Record<string, ArtifactItem>;
    loadingArtifacts: Record<string, ArtifactLoadingPlaceholder>;

    hiddenArtifactIds: Set<string>;

    // UI Actions
    openPanel: () => void;
    closePanel: () => void;
    togglePanel: () => void;
    setPanelWidth: (width: number) => void;
    setActiveArtifact: (id: string | null) => void;

    // Data Actions
    addArtifact: (artifact: Omit<ArtifactItem, "id" | "createdAt">) => string;
    updateArtifact: (id: string, updates: Partial<ArtifactItem>) => void;
    removeArtifact: (id: string) => void;
    restoreArtifact: (id: string) => void;

    // Loading Actions
    startLoading: (placeholder: { type: ArtifactType; title: string; threadId: string; language?: string }) => string;
    updateLoadingContent: (
        id: string,
        partialContent: {
            content: string;
            language?: string;
        }
    ) => void;
    finishLoading: (loadingId: string, artifact: Omit<ArtifactItem, "id" | "createdAt">) => void;
    cancelLoading: (loadingId: string) => void;

    // Selectors
    getThreadArtifacts: (threadId: string) => ArtifactItem[];
    getThreadLoadingArtifacts: (threadId: string) => ArtifactLoadingPlaceholder[];
    getAllThreadItems: (threadId: string) => (ArtifactItem | ArtifactLoadingPlaceholder)[];

    setHiddenArtifactIds: (ids: Set<string>) => void;
}

// ID generation utility
const generateId = (): string => `artifact-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// Helper to check for duplicate artifacts
const isDuplicateArtifact = (
    artifact: Omit<ArtifactItem, "id" | "createdAt">,
    existingArtifacts: ArtifactItem[]
): ArtifactItem | undefined => {
    return existingArtifacts.find(
        (existing) =>
            existing.title === artifact.title &&
            existing.type === artifact.type &&
            existing.threadId === artifact.threadId &&
            JSON.stringify(existing.content) === JSON.stringify(artifact.content)
    );
};

export const useArtifactStore = create<ArtifactState>()(
    devtools(
        persist(
            (set, get) => ({
                // Initial state
                isOpen: false,
                activeArtifactId: null,
                panelWidth: 50,
                artifacts: {},
                loadingArtifacts: {},
                hiddenArtifactIds: new Set<string>(),
                // UI Actions
                openPanel: () => {
                    const state = get();
                    // If no active artifact, set the newest available one
                    if (!state.activeArtifactId) {
                        const allItems = [...Object.keys(state.artifacts), ...Object.keys(state.loadingArtifacts)];
                        if (allItems.length > 0) {
                            // Get the newest artifact by finding the one with the latest createdAt
                            const newestId = allItems.reduce((newest, currentId) => {
                                const current = state.artifacts[currentId] || state.loadingArtifacts[currentId];
                                const newestItem = state.artifacts[newest] || state.loadingArtifacts[newest];
                                
                                if (!newestItem || new Date(current.createdAt) > new Date(newestItem.createdAt)) {
                                    return currentId;
                                }
                                return newest;
                            });
                            
                            set({ isOpen: true, activeArtifactId: newestId });
                            return;
                        }
                    }
                    set({ isOpen: true });
                },

                closePanel: () => set({ isOpen: false }),

                togglePanel: () => set((state) => ({ isOpen: !state.isOpen })),

                setPanelWidth: (width) =>
                    set({
                        panelWidth: Math.max(20, Math.min(80, width)),
                    }),

                setActiveArtifact: (id) => {
                    const state = get();
                    // Validate that the artifact exists
                    if (id && !state.artifacts[id] && !state.loadingArtifacts[id]) {
                        console.warn("Artifact not found:", id);
                        return;
                    }
                    set({ activeArtifactId: id });
                },

                // Data Actions
                addArtifact: (artifactData) => {
                    const state = get();
                    const existingArtifacts = Object.values(state.artifacts).filter(
                        (a) => a.threadId === artifactData.threadId
                    );

                    // Check for duplicates
                    const duplicate = isDuplicateArtifact(artifactData, existingArtifacts);
                    if (duplicate) {
                        // If duplicate is hidden, restore it
                        const newHidden = new Set(state.hiddenArtifactIds);
                        if (newHidden.has(duplicate.id)) {
                            newHidden.delete(duplicate.id);
                        }
                        
                        set({ 
                            activeArtifactId: duplicate.id, 
                            isOpen: true,
                            hiddenArtifactIds: newHidden
                        });
                        return duplicate.id;
                    }

                    // Create new artifact
                    const id = generateId();
                    const artifact: ArtifactItem = {
                        ...artifactData,
                        id,
                        createdAt: new Date(),
                    };

                    set({
                        artifacts: { ...state.artifacts, [id]: artifact },
                        activeArtifactId: id,
                        isOpen: true,
                    });

                    return id;
                },

                updateArtifact: (id, updates) => {
                    set((state) => {
                        if (!state.artifacts[id]) {
                            console.warn("Cannot update non-existent artifact:", id);
                            return state;
                        }

                        return {
                            artifacts: {
                                ...state.artifacts,
                                [id]: { ...state.artifacts[id], ...updates },
                            },
                        } as any;
                    });
                },

                removeArtifact: (id) => {
                    set((state) => {
                        // Create a new Set so we don’t mutate previous state
                        const newHidden = new Set(state.hiddenArtifactIds);
                        newHidden.add(id);

                        // Recompute which IDs remain visible
                        const remaining = [
                            ...Object.keys(state.artifacts),
                            ...Object.keys(state.loadingArtifacts),
                        ].filter((i) => !newHidden.has(i));

                        const newActive = remaining.length > 0 ? 
                            remaining.reduce((newest, currentId) => {
                                const current = state.artifacts[currentId] || state.loadingArtifacts[currentId];
                                const newestItem = state.artifacts[newest] || state.loadingArtifacts[newest];
                                
                                if (!newestItem || new Date(current.createdAt) > new Date(newestItem.createdAt)) {
                                    return currentId;
                                }
                                return newest;
                            }) : null;

                        return {
                            hiddenArtifactIds: newHidden,
                            activeArtifactId: newActive,
                            isOpen: newActive !== null,
                        };
                    });
                },

                restoreArtifact: (id: string) => {
                    set((state) => {
                        const newHidden = new Set(state.hiddenArtifactIds);
                        newHidden.delete(id);
                        return { hiddenArtifactIds: newHidden };
                    });
                },

                // Loading Actions
                startLoading: (placeholderData) => {
                    const id = generateId();
                    const placeholder: ArtifactLoadingPlaceholder = {
                        ...placeholderData,
                        id,
                        isLoading: true,
                        createdAt: new Date(),
                        isStreamingToPanel: true,
                    };

                    set((state) => ({
                        loadingArtifacts: {
                            ...state.loadingArtifacts,
                            [id]: placeholder,
                        },
                        activeArtifactId: id,
                        isOpen: true,
                    }));

                    return id;
                },

                updateLoadingContent: (id, partialContent) => {
                    set((state) => {
                        const loadingArtifact = state.loadingArtifacts[id];
                        if (!loadingArtifact) {
                            console.warn("Cannot update non-existent loading artifact:", id);
                            return state;
                        }

                        return {
                            loadingArtifacts: {
                                ...state.loadingArtifacts,
                                [id]: {
                                    ...loadingArtifact,
                                    partialContent,
                                },
                            },
                        };
                    });
                },

                finishLoading: (loadingId, artifactData) => {
                    set((state) => {
                        const placeholder = state.loadingArtifacts[loadingId];
                        if (!placeholder) {
                            console.warn("Cannot finish non-existent loading artifact:", loadingId);
                            return state;
                        }

                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { [loadingId]: removed, ...remainingLoading } = state.loadingArtifacts;

                        const artifact: ArtifactItem = {
                            ...artifactData,
                            id: loadingId,
                            createdAt: placeholder.createdAt,
                        };

                        return {
                            loadingArtifacts: remainingLoading,
                            artifacts: {
                                ...state.artifacts,
                                [loadingId]: artifact,
                            },
                        };
                    });
                },

                cancelLoading: (loadingId) => {
                    set((state) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { [loadingId]: removed, ...remainingLoading } = state.loadingArtifacts;

                        let newActiveId = state.activeArtifactId;
                        if (state.activeArtifactId === loadingId) {
                            const allIds = [...Object.keys(state.artifacts), ...Object.keys(remainingLoading)];
                            newActiveId = allIds.length > 0 ? 
                                allIds.reduce((newest, currentId) => {
                                    const current = state.artifacts[currentId] || remainingLoading[currentId];
                                    const newestItem = state.artifacts[newest] || remainingLoading[newest];
                                    
                                    if (!newestItem || new Date(current.createdAt) > new Date(newestItem.createdAt)) {
                                        return currentId;
                                    }
                                    return newest;
                                }) : null;
                        }

                        return {
                            loadingArtifacts: remainingLoading,
                            activeArtifactId: newActiveId,
                            isOpen: newActiveId !== null,
                        };
                    });
                },

                // Selectors
                // Selectors now filter by .has()
                getThreadArtifacts: (threadId) => {
                    const { artifacts, hiddenArtifactIds } = get();
                    return Object.values(artifacts)
                        .filter((a) => a.threadId === threadId && !hiddenArtifactIds.has(a.id))
                        .sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
                },

                getThreadLoadingArtifacts: (threadId) => {
                    const { loadingArtifacts, hiddenArtifactIds } = get();
                    return Object.values(loadingArtifacts)
                        .filter((l) => l.threadId === threadId && !hiddenArtifactIds.has(l.id))
                        .sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
                },

                getAllThreadItems: (threadId) => {
                    // merge both, then sort & filter
                    const arts = get().getThreadArtifacts(threadId);
                    const loads = get().getThreadLoadingArtifacts(threadId);
                    return [...arts, ...loads].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
                },
                setHiddenArtifactIds: (ids: Set<string>) => set({ hiddenArtifactIds: ids }),
            }),
            {
                name: "artifact-storage",
                // only persist your “real” data
                partialize: (state) => ({
                    artifacts: state.artifacts,
                    hiddenArtifactIds: Array.from(state.hiddenArtifactIds),
                    panelWidth: state.panelWidth,
                }),
                onRehydrateStorage: (state) => (persistedState) => {
                    if (!persistedState) return;
                    const arr = (persistedState as any).hiddenArtifactIds as string[] | undefined;
                    if (Array.isArray(arr)) {
                        state.setHiddenArtifactIds(new Set(arr));
                    }
                },
            }
        )
    )
);
